import { useEffect, useState } from "react";

import { useTranslator } from "@eo-locale/react";

import { object, number, boolean } from "yup";

import { isValid, parse } from "date-fns";

import {
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  FormErrorIcon,
  HTMLWrapper,
  HStack,
  Input,
  VisuallyHidden
} from "@/storybook-components";
import type { Question } from "@/types";
import { setAnswer, setError } from "@/redux/features/questions/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

//schema to validate date of birth
const ageSchemaValidatorObject = object().shape({
  day: number()
    .when("isrequired", {
      is: (val) => val === true,
      then: number().required("blank"),
      otherwise: number().notRequired()
    })
    .min(1, "invalidDay")
    .max(31, "invalidDay"),
  month: number()
    .when("isrequired", {
      is: (val) => val === true,
      then: number().required("blank"),
      otherwise: number().notRequired()
    })
    .min(1, "invalidMonth")
    .max(12, "invalidMonth"),
  year: number()
    .when("isrequired", {
      is: (val) => val === true,
      then: number().required("blank"),
      otherwise: number().notRequired()
    })
    .min(1900, "invalidYear")
    .max(new Date().getFullYear(), "invalidYear")
});

//schema to validate only year when Year of birth is selected
const yearSchemaValidatorObject = object().shape({
  year: number()
    .min(1900, "invalidYear")
    .max(new Date().getFullYear(), "invalidYear")
    .when("isrequired", {
      is: (val) => val === true,
      then: number().required("blank"),
      otherwise: number().notRequired()
    })
});

//schema to validate correctness of date
const dateSchema = boolean()
  .test("is valid date", "invalidDate", (val) => val === true)
  .nullable();

//schema to validate year length in mobile browser
const yearLength = number()
  .test("length", "invalidDate", (val) => `${val}`.length === 4)
  .nullable();

interface DateType {
  day?: number;
  month?: number;
  year?: number;
}

export const validate = async (
  value: DateType,
  question: Question
): Promise<string[]> => {
  let schema = object().nullable();

  schema = question.yearOnlyDate
    ? yearSchemaValidatorObject.required()
    : ageSchemaValidatorObject.required();

  try {
    const dateObject = { ...value, isrequired: question.required };

    await schema.validate(dateObject);

    const formattedDate = parse(
      `${value.day}.${value.month}.${value.year}`,
      "dd.MM.yyyy",
      new Date()
    );
    const isFutureDate = formattedDate > new Date();
    const isValidDate = isValid(formattedDate) && !isFutureDate;
    const isDateUndefined = Object.values(value).every(
      (el) => el === undefined
    );

    if (!isDateUndefined && !question.yearOnlyDate)
      await dateSchema.validate(isValidDate);

    if (question.yearOnlyDate && !!dateObject.year) {
      await yearLength.validate(dateObject.year);
    }

    return [];
  } catch (err) {
    return err.errors;
  }
};

interface AgeQuestionProps {
  question: Partial<Question>;
}

const AgeQuestion = ({ question }: AgeQuestionProps): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const { answers, errors, status } = useAppSelector(
    (state) => state.questions
  );
  const { site } = useAppSelector((state) => state.site);
  const error = errors[question.id] || [];
  const answer = answers[question.id] || "";

  const [day, setDay] = useState(answer?.day || 0);
  const [month, setMonth] = useState(answer?.month || 0);
  const [year, setYear] = useState(answer?.year || 0);
  const isRegionUS = site?.region.toUpperCase() === "UNITED_STATES";

  //To set the dates when answer gets updated in profile
  useEffect(() => {
    setDay(answer.day);
    setMonth(answer.month);
    setYear(answer.year);
  }, [answer]);

  const handleChange = (): void => {
    dispatch(
      setAnswer({
        questionId: question.id,
        //required for yup validation
        answer: {
          day: day !== 0 ? day : undefined,
          month: month !== 0 ? month : undefined,
          year: year !== 0 ? year : undefined
        }
      })
    );
  };

  const validateLength = (ele) => {
    let val = ele.value;
    if (val.length > ele.maxLength) val = val.slice(0, ele.maxLength);
    return val;
  };

  const onCalenderChange = (e, setMethod) => {
    //Remove error msg onChange event
    dispatch(
      setError({
        questionId: question.id,
        error: ""
      })
    );
    let value = validateLength(e.target);
    setMethod(+value);
  };

  const isValidData = (invalidMethod, calendarDetails) =>
    error[0] === invalidMethod ||
    (!calendarDetails && error[0] === "blank") ||
    error[0] === "invalidDate";

  const blockInvalidChar = (e) => {
    //Allowing: Integers | Backspace | Tab | Delete | Left & Right arrow keys
    const regex = new RegExp(
      /(^\d*$)|(Backspace|Tab|Delete|ArrowLeft|ArrowRight)/
    );

    return !e.key.match(regex) && e.preventDefault();
  };
  const renderDay = () => {
    return (
      <Input
        placeholder={translate("common.day")}
        type="number"
        value={+day || ""}
        onChange={(e) => onCalenderChange(e, setDay)}
        onBlur={handleChange}
        onKeyDown={blockInvalidChar}
        maxLength={2}
        //Removed paste functionaly as pasting an incorrect value with 'e' would be considered empty string and may cause validation to break
        onPaste={(e) => e.preventDefault()}
        data-testid="ageQuestion-dayInput"
        name="ageQuestion-dayInput"
        autoComplete="off"
      />
    );
  };

  const renderMonth = () => {
    return (
      <Input
        placeholder={translate("common.month")}
        type="number"
        value={+month || ""}
        onChange={(e) => onCalenderChange(e, setMonth)}
        onBlur={handleChange}
        onKeyDown={blockInvalidChar}
        maxLength={2}
        //Removed paste functionaly as pasting an incorrect value with 'e' would be considered empty string and may cause validation to break
        onPaste={(e) => e.preventDefault()}
        data-testid="ageQuestion-monthInput"
        name="ageQuestion-monthInput"
        autoComplete="off"
      />
    );
  };

  const renderYear = () => {
    return (
      <FormControl
        isDisabled={status === "saving"}
        isInvalid={isValidData("invalidYear", year)}
      >
        <Input
          placeholder={translate("common.year")}
          type="number"
          value={+year || ""}
          onChange={(e) => onCalenderChange(e, setYear)}
          onBlur={handleChange}
          onKeyDown={blockInvalidChar}
          maxLength={4}
          //Removed paste functionaly as pasting an incorrect value with 'e' would be considered empty string and may cause validation to break
          onPaste={(e) => e.preventDefault()}
          data-testid="ageQuestion-yearInput"
          name="ageQuestion-yearInput"
          autoComplete="off"
        />
      </FormControl>
    );
  };

  const renderDateOfBirth = () => {
    return (
      <HStack spacing={7}>
        <FormControl
          isInvalid={
            isRegionUS
              ? isValidData("invalidMonth", month)
              : isValidData("invalidDay", day)
          }
          isDisabled={status === "saving"}
        >
          {isRegionUS ? renderMonth() : renderDay()}
        </FormControl>
        <FormControl
          isInvalid={
            isRegionUS
              ? isValidData("invalidDay", day)
              : isValidData("invalidMonth", month)
          }
          isDisabled={status === "saving"}
        >
          {isRegionUS ? renderDay() : renderMonth()}
        </FormControl>
        {renderYear()}
      </HStack>
    );
  };

  return (
    <FormControl
      my={4}
      id={`${question.id}`}
      isInvalid={error.length > 0}
      isRequired={question.required}
      isDisabled={status === "saving"}
      className={error.length > 0 ? "ehqQuestionError" : null}
    >
      <FormLabel>
        <HTMLWrapper html={question.question} />
        {question.required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </FormLabel>

      {question.yearOnlyDate ? renderYear() : renderDateOfBirth()}

      {error.length > 0 && (
        <FormErrorMessage data-testid="ageQuestion-errorMessage">
          <FormErrorIcon />
          {translate(`errors.${error}`)}
        </FormErrorMessage>
      )}
      <FormHelperText>{question.notes}</FormHelperText>
    </FormControl>
  );
};

export default AgeQuestion;
